<template>
  <v-dialog v-model="dialog" max-width="1200px">
    <template v-slot:activator="{ on }">
      <v-btn color="primary" v-on="on">Manage Roles</v-btn>
    </template>
    <v-card>
      <v-row cols="12" class="v-card v-sheet theme--light mx-auto fill-height">
        <v-col>
          <v-data-table
            :headers="headers"
            :items="roles"
            :loading="isLoading"
            :disable-pagination="true"
            fixed-header
            hide-default-footer
            item-key="role_id"
            class="th-nowrap"
            :height="`calc(100vh - 200px)`"
          >

            <template v-slot:item.entity_id="{ item }">
              {{ projectName(item.entity_id) }}
            </template>
            <template v-slot:top>
              <v-row class="mx-auto">
                <h3 style="line-height: 36px">Roles</h3>
                <v-spacer />

                <v-btn
                  class="mt-0"
                  color="success"
                  @click="newRole()">
                  <v-icon left>mdi-pencil</v-icon>
                  New
                </v-btn>
                <v-btn
                  color="green darken-2"
                  small
                  icon
                  class="ml-3"
                  style="margin-right: -7px; margin-top: -7px"
                  @click="dialog = false"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-row>
            </template>

            <template #item.actions="{ item }">
              <div class="d-flex">
                <v-btn
                  v-if="canViewRoleHistory"
                  icon
                  color="primary"
                  @click="$emit('history', item.role_id)"
                >
                  <v-icon>
                    history
                  </v-icon>
                </v-btn>
                <v-btn
                  color="green"
                  icon
                  @click="editRole(item)"
                >
                  <v-icon>edit</v-icon>
                </v-btn>
                <v-btn
                  v-if="$store.state.auth.checkAccess({ key: 'role_deleting', value: true })"
                  color="green"
                  icon
                  @click="deleteRole(item.role_id)"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </div>
            </template>

          </v-data-table>
        </v-col>

        <drawer-dialog
          v-model="isUpserting"
          width="70%"
          :is-loading="isLoading"
        >
          <role-form
            v-if="isUpserting && role"
            :role="role"
            :project-list="projectList"
            :permissions-by-group="permissionsByGroup"
            :entities-by-id="entitiesById"
            :is-loading="isLoading"
            :readonly="isLoading"
            @close="isUpserting = false, role = null"
            @save="save($event)"
          />
        </drawer-dialog>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<style scoped>
</style>

<script>
  import { map, find, groupBy } from 'lodash'
  import { API } from '@/utils/api'
  import { RoleForm } from '../../modules/roles'
  import { DrawerDialog } from '../core/DrawerDialog'

  export default {
    props: {
      entitiesById: {
        type: Object,
        required: true,
      },
      canViewRoleHistory: Boolean,
    },
    components: {
      RoleForm,
      DrawerDialog
    },

    data: () => ({
      dialog: false,
      headers: [
        { text: 'ID', value: 'role_id', sortable: true },
        { text: 'Base', value: 'entity_id', sortable: true },
        { text: 'Name', value: 'name', sortable: true },
        { text: 'Description', sortable: false, value: 'description' },
        { text: '', sortable: false, value: 'actions', width: '8px' }
      ],
      isLoading: false,
      isUpserting: false,
      loadingRole: false,
      editTarget: null,
      permissionsByGroup: {},
      selected: [],
      roles: [],
      role: false,
    }),

    mounted () {
      this.initialize()
    },

    methods: {
      async initialize () {
        const promises = [
          this.loadPermissionList(),
          this.loadRoles(),
        ]

        this.isLoading = true
        try {
          await Promise.all(promises)
        } finally {
          this.isLoading = false
        }
      },
      projectName (entity_id) {
        return (find(this.projectList, { value: entity_id }) || { text: entity_id }).text
      },

      async save (role) {
        this.isLoading = true

        try {
          await API.call('/users/role/update.json', { role, entity_id: role.entity_id })
          await this.loadRoles()
        } finally {
          this.role = null
          this.isLoading = false
        }
      },

      modifyPermissionResponse (permissionsByKey) {
        for (const key in permissionsByKey) {
          permissionsByKey[key].key = key
          permissionsByKey[key].group = permissionsByKey[key].group ?? 'general'
        }
      },

      async loadPermissionList () {
        const { data } = await API.call('/users/permissions.json')
        this.modifyPermissionResponse(data)
        this.permissionsByGroup = groupBy(data, 'group')
      },

      async loadRoles () {
        const { data } = await API.call('/users/role/list.json')
        this.roles = data
      },

      newRole () {
        this.role = { new: true, permissions: {} }
        this.isUpserting = true
      },
      editRole (role) {
        this.role = role
        this.isUpserting = true
      },
      async deleteRole (role_id) {
        if (await this.$root.$confirm('Confirm', 'Are you sure you want to delete this item?')) {
          this.isLoading = true
          try {
            await API.call('/users/role/delete.json', {role_id})

            this.$notification.show = true
            this.$notification.message = `Role deletion is successful`
            this.$notification.type = 'success'
          } catch {
            this.$notification.show = true
            this.$notification.message = 'Failed to delete role'
            this.$notification.type = 'error'
          } finally {
            await this.loadRoles ()
            this.isLoading = false
          }

        }
      }
    },

    computed: {
      projectList: function () {
        return [ { text: 'Default', value: null }, ...(map(this.entities || this.$store.state.auth.entities, (i) => ({ text: i.name, value: i.entity_id }))) ]
      }
    },
  }

</script>
